import { isOfflineRecordRevision } from '@scrambl/shared/offline'

export interface TaskReaderProjection {
  revision: number
  fields: string[]
  customFields: string[]
}

/** Only an explicit valid reader mask can retire fields in a sparse patch. */
export function taskReaderProjection(
  value: unknown,
): TaskReaderProjection | null {
  if (!value || typeof value !== 'object') return null
  const projection = (value as Record<string, unknown>)._readerProjection
  if (!projection || typeof projection !== 'object') return null
  const input = projection as Record<string, unknown>
  const names = (value: unknown): value is string[] =>
    Array.isArray(value) &&
    value.every(name => typeof name === 'string' && name.length > 0)
  if (
    !isOfflineRecordRevision(input.revision) ||
    !names(input.fields) ||
    !names(input.customFields) ||
    !input.fields.includes('id') ||
    !input.fields.includes('organizationId') ||
    !input.fields.includes('revision')
  )
    return null
  return {
    revision: input.revision,
    fields: input.fields,
    customFields: input.customFields,
  }
}

/** Preserve permitted cached values; remove values outside the current reader. */
export function pruneTaskReaderFields(
  row: Record<string, unknown>,
  projection: TaskReaderProjection,
): Record<string, unknown> {
  const allowed = new Set(projection.fields)
  const allowedCustom = new Set(projection.customFields)
  const result: Record<string, unknown> = {}
  for (const [field, value] of Object.entries(row)) {
    if (field === '_realtime_updated' && typeof value === 'number') {
      result[field] = value
      continue
    }
    if (!allowed.has(field)) continue
    if (field === 'customFields') {
      result[field] =
        value && typeof value === 'object' && !Array.isArray(value)
          ? Object.fromEntries(
              Object.entries(value).filter(([name]) => allowedCustom.has(name)),
            )
          : {}
    } else result[field] = value
  }
  result._readerProjection = projection
  const keys = Object.keys(result)
  // Duplicate subscribers must not repeatedly publish an unchanged redaction.
  const previous = taskReaderProjection(row)
  const sameProjection =
    previous &&
    previous.revision === projection.revision &&
    JSON.stringify(previous.fields) === JSON.stringify(projection.fields) &&
    JSON.stringify(previous.customFields) ===
      JSON.stringify(projection.customFields)
  const unchanged =
    keys.length === Object.keys(row).length &&
    keys.every(key =>
      key === '_readerProjection'
        ? sameProjection
        : key === 'customFields'
          ? row[key] &&
            typeof row[key] === 'object' &&
            !Array.isArray(row[key]) &&
            Object.keys(row[key] as object).length ===
              Object.keys(result[key] as object).length &&
            Object.entries(result[key] as object).every(
              ([name, value]) =>
                (row[key] as Record<string, unknown>)[name] === value,
            )
          : row[key] === result[key],
    )
  return unchanged ? row : result
}
